import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DeliveryStatus = "unassigned" | "assigned" | "in_transit" | "delivered";

export interface DeliveryOrder {
  readonly id: string;
  readonly public_ref: string;
  readonly customer_name: string;
  readonly customer_phone: string;
  readonly delivery_address: string;
  readonly total_ttc: number;
  readonly payment_method: string;
  readonly created_at: string;
}

export interface DeliveryDriver {
  readonly id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly vehicle_type: string;
  readonly is_available?: boolean;
}

export interface DeliveryZone {
  readonly id: string;
  readonly name: string;
  readonly code: string;
}

export interface Delivery {
  readonly id: string;
  readonly status: DeliveryStatus;
  readonly estimated_at: string | null;
  readonly delivered_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly driver_id: string | null;
  readonly zone_id: string | null;
  readonly address: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly notes: string | null;
  readonly order: DeliveryOrder;
  readonly driver: DeliveryDriver | null;
  readonly zone: DeliveryZone | null;
}

export interface DeliveryListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: string | null;
  readonly zone_id?: string | null;
  readonly driver_id?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface DeliveryListResponse {
  readonly deliveries: readonly Delivery[];
  readonly drivers: readonly DeliveryDriver[];
  readonly zones: readonly DeliveryZone[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface AssignDriverPayload {
  readonly driver_id: string;
  readonly estimated_at?: string;
  readonly notes?: string;
}

export interface AssignDriverResponse {
  readonly delivery: Delivery;
}

// ---------------------------------------------------------------------------
// Performance report types
// ---------------------------------------------------------------------------

export interface DeliveryDriverStat {
  readonly driver_id: string;
  readonly name: string;
  readonly delivered: number;
  readonly failed: number;
  readonly total: number;
  readonly success_rate: number;
  readonly avg_delay_minutes: number | null;
}

export interface DeliveryZoneStat {
  readonly zone: string;
  readonly total: number;
  readonly delivered: number;
  readonly failed: number;
  readonly on_time_rate: number | null;
  readonly avg_delivery_minutes: number | null;
  readonly cost_total: number;
}

export interface DeliveryTrendPoint {
  readonly date: string;
  readonly total: number;
  readonly delivered: number;
  readonly failed: number;
  readonly on_time_rate: number | null;
}

export interface DeliveryPerformanceData {
  readonly summary: {
    readonly total_deliveries: number;
    readonly delivered: number;
    readonly in_progress: number;
    readonly failed: number;
    readonly on_time_rate: number | null;
    readonly avg_delivery_minutes: number | null;
    readonly total_cost: number;
    readonly cost_per_delivery: number | null;
  };
  readonly by_status: ReadonlyArray<{ readonly status: string; readonly count: number }>;
  readonly by_method: { readonly own_fleet: number; readonly yango: number };
  readonly driver_performance: ReadonlyArray<DeliveryDriverStat>;
  readonly zone_performance: ReadonlyArray<DeliveryZoneStat>;
  readonly trend: ReadonlyArray<DeliveryTrendPoint>;
}

export interface DeliveryPerformanceParams {
  readonly from: string;
  readonly to: string;
  readonly region_id?: string;
}

// ---------------------------------------------------------------------------
// Delivery zone management types
// ---------------------------------------------------------------------------

export interface AdminDeliveryZone {
  readonly id: string;
  readonly region_id: string;
  readonly zone_name: string;
  readonly fee_own_car_fcfa: number;
  readonly fee_own_bike_fcfa: number | null;
  readonly fee_yango_estimated_fcfa: number | null;
  readonly estimated_time_minutes: number;
  readonly is_serviceable: boolean;
  readonly centroid_lat: number | null;
  readonly centroid_lng: number | null;
  readonly region: {
    readonly name: string;
    readonly code: string;
  } | null;
}

export interface AdminDeliveryZoneListResponse {
  readonly zones: readonly AdminDeliveryZone[];
}

export interface UpdateDeliveryZonePayload {
  readonly fee_own_car_fcfa?: number;
  readonly fee_own_bike_fcfa?: number | null;
  readonly fee_yango_estimated_fcfa?: number | null;
  readonly estimated_time_minutes?: number;
  readonly is_serviceable?: boolean;
  readonly centroid_lat?: number | null;
  readonly centroid_lng?: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Driver types
// ---------------------------------------------------------------------------

export type DriverVehicleType = "moto" | "voiture" | "camionnette" | "velo";

export interface Driver {
  readonly id: string;
  readonly full_name: string;
  readonly phone: string;
  readonly vehicle_type: DriverVehicleType;
  readonly region: string;
  readonly is_available: boolean;
  readonly created_at: string;
}

export interface DriverListResponse {
  readonly drivers: readonly Driver[];
}

export interface DriverPayload {
  readonly full_name: string;
  readonly phone: string;
  readonly vehicle_type: DriverVehicleType;
  readonly region: string;
  readonly is_available: boolean;
}

// ---------------------------------------------------------------------------
// Route optimizer types
// ---------------------------------------------------------------------------

export interface RouteStop {
  readonly stop_number: number;
  readonly order_ref: string;
  readonly address: string;
  readonly customer_name: string;
  readonly estimated_time: string | null;
  readonly notes: string | null;
}

export interface OptimizedRoute {
  readonly driver_id: string;
  readonly driver_name: string;
  readonly date: string;
  readonly stops: readonly RouteStop[];
  readonly total_distance_km: number | null;
  readonly estimated_duration_minutes: number | null;
}

export interface RouteOptimizePayload {
  readonly driver_id: string;
  readonly date: string;
}

// ---------------------------------------------------------------------------
// Delivery slot types
// ---------------------------------------------------------------------------

export interface DeliverySlot {
  readonly id: string;
  readonly zone_id: string;
  readonly zone_name: string;
  readonly label: string;
  readonly start_hour: number;
  readonly end_hour: number;
  readonly capacity: number;
  readonly days_of_week: readonly number[];
  readonly is_active: boolean;
}

export interface DeliverySlotListResponse {
  readonly slots: readonly DeliverySlot[];
}

export interface DeliverySlotPayload {
  readonly zone_id: string;
  readonly label: string;
  readonly start_hour: number;
  readonly end_hour: number;
  readonly capacity: number;
  readonly days_of_week: readonly number[];
  readonly is_active: boolean;
}

// ---------------------------------------------------------------------------
// Depot types
// ---------------------------------------------------------------------------

export interface Depot {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  readonly region: string;
  readonly phone: string;
  readonly manager_name: string;
  readonly is_active: boolean;
}

export interface DepotListResponse {
  readonly depots: readonly Depot[];
}

export interface DepotPayload {
  readonly name: string;
  readonly address: string;
  readonly region: string;
  readonly phone: string;
  readonly manager_name: string;
  readonly is_active: boolean;
}

// ---------------------------------------------------------------------------
// Proof of delivery types
// ---------------------------------------------------------------------------

export interface DeliveryProof {
  readonly photo_url: string | null;
  readonly signature_url: string | null;
  readonly captured_at: string | null;
  readonly flagged_for_followup: boolean;
}

export type DeliveryProofStatus = "photo" | "signature" | "both" | "missing";

export interface DeliveryWithProof extends Delivery {
  readonly proof: DeliveryProof | null;
  readonly proof_status: DeliveryProofStatus;
}

export interface DeliveryWithProofListResponse {
  readonly deliveries: readonly DeliveryWithProof[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface DeliveryWithProofListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly proof_status?: DeliveryProofStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export const deliveryApi = {
  /** Fetch deliveries for the Kanban board with optional filters. */
  list: (params: DeliveryListParams = {}) => {
    const qs = toQueryString({
      page: params.page,
      limit: params.limit ?? 100,
      status: params.status,
      zone_id: params.zone_id,
      driver_id: params.driver_id,
      date_from: params.date_from,
      date_to: params.date_to,
    });
    return adminFetch<DeliveryListResponse>(`/api/admin/deliveries${qs}`);
  },

  /** Assign a driver to a delivery. */
  assign: (deliveryId: string, payload: AssignDriverPayload) =>
    adminFetch<AssignDriverResponse>(
      `/api/admin/deliveries/${encodeURIComponent(deliveryId)}/assign`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    ),

  /** Fetch delivery performance report for a given date range. */
  getPerformance: (params: DeliveryPerformanceParams) => {
    const qs = toQueryString({
      from: params.from,
      to: params.to,
      region_id: params.region_id,
    });
    return adminFetch<DeliveryPerformanceData>(
      `/api/admin/reports/delivery${qs}`,
    );
  },

  // ---------------------------------------------------------------------------
  // Drivers CRUD
  // ---------------------------------------------------------------------------

  drivers: {
    list: () => adminFetch<DriverListResponse>("/api/admin/drivers"),

    create: (payload: DriverPayload) =>
      adminFetch<{ driver: Driver }>("/api/admin/drivers", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    update: (id: string, payload: Partial<DriverPayload>) =>
      adminFetch<{ driver: Driver }>(
        `/api/admin/drivers/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        },
      ),

    delete: (id: string) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/drivers/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  // ---------------------------------------------------------------------------
  // Route optimizer
  // ---------------------------------------------------------------------------

  routes: {
    optimize: (payload: RouteOptimizePayload) =>
      adminFetch<OptimizedRoute>("/api/admin/logistics/routes", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  },

  // ---------------------------------------------------------------------------
  // Delivery slots
  // ---------------------------------------------------------------------------

  slots: {
    list: () => adminFetch<DeliverySlotListResponse>("/api/admin/delivery/slots"),

    create: (payload: DeliverySlotPayload) =>
      adminFetch<{ slot: DeliverySlot }>("/api/admin/delivery/slots", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    update: (id: string, payload: Partial<DeliverySlotPayload>) =>
      adminFetch<{ slot: DeliverySlot }>(
        `/api/admin/delivery/slots/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        },
      ),

    delete: (id: string) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/delivery/slots/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  // ---------------------------------------------------------------------------
  // Depots
  // ---------------------------------------------------------------------------

  depots: {
    list: () => adminFetch<DepotListResponse>("/api/admin/depots"),

    create: (payload: DepotPayload) =>
      adminFetch<{ depot: Depot }>("/api/admin/depots", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    update: (id: string, payload: Partial<DepotPayload>) =>
      adminFetch<{ depot: Depot }>(
        `/api/admin/depots/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        },
      ),
  },

  // ---------------------------------------------------------------------------
  // Delivery zones
  // ---------------------------------------------------------------------------

  zones: {
    list: () =>
      adminFetch<AdminDeliveryZoneListResponse>("/api/admin/delivery/zones"),

    update: (id: string, payload: UpdateDeliveryZonePayload) =>
      adminFetch<AdminDeliveryZone>(
        `/api/admin/delivery/zones/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        },
      ),
  },

  // ---------------------------------------------------------------------------
  // Proof of delivery
  // ---------------------------------------------------------------------------

  proofs: {
    list: (params: DeliveryWithProofListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        proof_status: params.proof_status,
        date_from: params.date_from,
        date_to: params.date_to,
        include_proof: true,
      });
      return adminFetch<DeliveryWithProofListResponse>(
        `/api/admin/deliveries${qs}`,
      );
    },

    flag: (deliveryId: string, flagged: boolean) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/deliveries/${encodeURIComponent(deliveryId)}/proof/flag`,
        {
          method: "PATCH",
          body: JSON.stringify({ flagged_for_followup: flagged }),
        },
      ),
  },
} as const;
